import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { apiClient, Tenant, CreateTenantDto, UpdateTenantDto, TenantStatus } from '../../services/api';
import { Building2, Plus, Edit2, Trash2, ArrowLeft, Search, Filter, Settings, LogOut } from 'lucide-react';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import ConfirmDialog, { useConfirmDialog } from '../../components/ui/ConfirmDialog';

export default function TenantManagement() {
  const navigate = useNavigate();
  const { user, accessToken, logout } = useAuth();
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<TenantStatus | 'all'>('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState<Tenant | null>(null);
  const [feedback, setFeedback] = useState<{type: 'success' | 'error', message: string} | null>(null);
  const { confirm: showConfirm, dialogProps } = useConfirmDialog();

  const isSuperAdmin = user?.role === 'super_admin';

  useEffect(() => {
    if (!isSuperAdmin) {
      navigate('/waba');
    }
  }, [isSuperAdmin, navigate]);

  useEffect(() => {
    if (isSuperAdmin) {
      loadTenants();
    }
  }, [isSuperAdmin]);

  const loadTenants = async () => {
    if (!accessToken) {
      return;
    }

    try {
      setLoading(true);
      const response = await apiClient.getTenants(
        {
          page: 1,
          limit: 100,
          ...(statusFilter !== 'all' && { status: statusFilter }),
        },
        accessToken
      );

      if (response.success) {
        setTenants(response.data.data);
      } else {
        setFeedback({ type: 'error', message: 'Erro ao carregar empresas' });
      }
    } catch {
      setFeedback({ type: 'error', message: 'Erro ao carregar empresas. Verifique suas permissões.' });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTenant = async (data: CreateTenantDto) => {
    if (!accessToken) return;

    try {
      const response = await apiClient.createTenant(data, accessToken);
      if (response.success) {
        setShowCreateModal(false);
        loadTenants();
      }
    } catch {
      setFeedback({ type: 'error', message: 'Erro ao criar empresa. Verifique os dados e tente novamente.' });
    }
  };

  const handleUpdateTenant = async (id: string, data: UpdateTenantDto) => {
    if (!accessToken) return;

    try {
      const response = await apiClient.updateTenant(id, data, accessToken);
      if (response.success) {
        setShowEditModal(false);
        setSelectedTenant(null);
        loadTenants();
      }
    } catch {
      setFeedback({ type: 'error', message: 'Erro ao atualizar empresa. Tente novamente.' });
    }
  };

  const handleDeleteTenant = (id: string) => {
    if (!accessToken) return;

    showConfirm({
      title: 'Deletar empresa',
      message: 'Tem certeza que deseja deletar esta empresa? Esta ação é irreversível.',
      variant: 'danger',
      confirmText: 'Deletar',
      onConfirm: async () => {
        try {
          await apiClient.deleteTenant(id, accessToken);
          loadTenants();
        } catch {
          setFeedback({ type: 'error', message: 'Erro ao deletar empresa. Tente novamente.' });
        }
      },
    });
  };

  const filteredTenants = (tenants || []).filter((tenant) => {
    const matchesSearch =
      tenant.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      tenant.email.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = statusFilter === 'all' || tenant.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  if (!isSuperAdmin) {
    return null;
  }

  const getStatusBadge = (status: TenantStatus) => {
    const colors = {
      active: 'bg-success/10 text-success',
      trial: 'bg-info/10 text-accent-1',
      inactive: 'bg-bg-3 text-text-1',
      suspended: 'bg-danger/10 text-danger',
    };

    const labels = {
      active: 'Ativo',
      trial: 'Trial',
      inactive: 'Inativo',
      suspended: 'Suspenso',
    };

    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors[status]}`}>
        {labels[status]}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-bg-1">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-6">
          <div className="flex justify-between items-center mb-4">
            <button
              onClick={() => navigate('/admin')}
              className="flex items-center text-text-2 hover:text-text-1"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Voltar ao Dashboard
            </button>
            <button
              onClick={() => {
                logout();
                navigate('/login');
              }}
              className="flex items-center gap-2 px-4 py-2 bg-danger hover:bg-danger/90 text-white rounded-lg transition-colors"
            >
              <LogOut className="w-4 h-4" />
              Sair
            </button>
          </div>

          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold text-text-1 flex items-center">
                <Building2 className="w-6 h-6 mr-2" />
                Gerenciar Empresas
              </h1>
              <p className="mt-1 text-sm text-text-2">
                Total: {filteredTenants.length} empresa(s)
              </p>
            </div>

            <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center px-4 py-2 bg-accent-1 text-white rounded-lg hover:bg-accent-1/90"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nova Empresa
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-bg-2 rounded-lg shadow p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-3 w-5 h-5" />
              <input
                type="text"
                placeholder="Buscar por nome ou email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-border-1 rounded-lg focus:ring-2 focus:ring-accent-1 focus:border-transparent"
              />
            </div>

            <div className="relative">
              <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-3 w-5 h-5" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as TenantStatus | 'all')}
                className="w-full pl-10 pr-4 py-2 border border-border-1 rounded-lg focus:ring-2 focus:ring-accent-1 focus:border-transparent"
              >
                <option value="all">Todos os status</option>
                <option value="active">Ativo</option>
                <option value="trial">Trial</option>
                <option value="inactive">Inativo</option>
                <option value="suspended">Suspenso</option>
              </select>
            </div>
          </div>
        </div>

        {/* Feedback Banner */}
        {feedback && (
          <div className={`mb-4 p-3 rounded-lg flex items-center justify-between ${
            feedback.type === 'error'
              ? 'bg-danger/10 border border-danger/30 text-danger'
              : 'bg-success/10 border border-success/30 text-success'
          }`}>
            <span className="text-sm">{feedback.message}</span>
            <button onClick={() => setFeedback(null)} className="ml-2 hover:opacity-70">
              &times;
            </button>
          </div>
        )}

        {/* Tenants Table */}
        {loading ? (
          <LoadingSpinner text="Carregando..." />
        ) : (
          <div className="bg-bg-2 rounded-lg shadow overflow-hidden">
            <table className="min-w-full divide-y divide-border-1">
              <thead className="bg-bg-3">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-text-3 uppercase tracking-wider">
                    Empresa
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-text-3 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-text-3 uppercase tracking-wider">
                    Plano
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-text-3 uppercase tracking-wider">
                    Criado em
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-text-3 uppercase tracking-wider">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody className="bg-bg-2 divide-y divide-border-1">
                {filteredTenants.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-6 py-8 text-center text-text-3">
                      Nenhuma empresa encontrada
                    </td>
                  </tr>
                ) : (
                  filteredTenants.map((tenant) => (
                    <tr key={tenant.id} className="hover:bg-bg-3/50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-text-1">
                            {tenant.name}
                          </div>
                          <div className="text-sm text-text-3">{tenant.email}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(tenant.status)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm text-text-1 capitalize">
                          {tenant.tier}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm text-text-3">
                          {new Date(tenant.createdAt).toLocaleDateString('pt-BR')}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          onClick={() => navigate(`/admin/tenants/${tenant.id}/environment`)}
                          className="text-success hover:text-success/80 mr-4"
                          title="Configurar empresa"
                        >
                          <Settings className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => {
                            setSelectedTenant(tenant);
                            setShowEditModal(true);
                          }}
                          className="text-accent-1 hover:text-accent-1/80 mr-4"
                          title="Editar empresa"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteTenant(tenant.id)}
                          className="text-danger hover:text-danger/80"
                          title="Deletar empresa"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <CreateTenantModal
          onClose={() => setShowCreateModal(false)}
          onSubmit={handleCreateTenant}
        />
      )}

      {/* Edit Modal */}
      {showEditModal && selectedTenant && (
        <EditTenantModal
          tenant={selectedTenant}
          onClose={() => {
            setShowEditModal(false);
            setSelectedTenant(null);
          }}
          onSubmit={(data) => handleUpdateTenant(selectedTenant.id, data)}
        />
      )}

      <ConfirmDialog {...dialogProps} />
    </div>
  );
}

// Create Modal Component
function CreateTenantModal({
  onClose,
  onSubmit,
}: {
  onClose: () => void;
  onSubmit: (data: CreateTenantDto) => void;
}) {
  const [formData, setFormData] = useState<CreateTenantDto>({
    name: '',
    email: '',
    status: 'trial',
    tier: 'standard',
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-bg-1 rounded-lg max-w-md w-full p-6">
        <h2 className="text-xl font-bold mb-4">Criar Nova Empresa</h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-text-2 mb-1">
              Nome *
            </label>
            <input
              type="text"
              required
              minLength={3}
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-3 py-2 border border-border-1 rounded-lg focus:ring-2 focus:ring-accent-1 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-text-2 mb-1">
              Email *
            </label>
            <input
              type="email"
              required
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="w-full px-3 py-2 border border-border-1 rounded-lg focus:ring-2 focus:ring-accent-1 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-text-2 mb-1">
              Status Inicial
            </label>
            <select
              value={formData.status}
              onChange={(e) => setFormData({ ...formData, status: e.target.value as TenantStatus })}
              className="w-full px-3 py-2 border border-border-1 rounded-lg focus:ring-2 focus:ring-accent-1 focus:border-transparent"
            >
              <option value="trial">Trial</option>
              <option value="active">Ativo</option>
              <option value="inactive">Inativo</option>
              <option value="suspended">Suspenso</option>
            </select>
          </div>

          <div className="flex justify-end space-x-3 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-border-1 rounded-lg hover:bg-bg-3/50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-accent-1 text-white rounded-lg hover:bg-accent-1/90"
            >
              Criar Empresa
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Edit Modal Component
function EditTenantModal({
  tenant,
  onClose,
  onSubmit,
}: {
  tenant: Tenant;
  onClose: () => void;
  onSubmit: (data: UpdateTenantDto) => void;
}) {
  const [formData, setFormData] = useState<UpdateTenantDto>({
    name: tenant.name,
    email: tenant.email,
    status: tenant.status,
    tier: tenant.tier,
    billingCycleDay: tenant.billingCycleDay || 1,
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-bg-1 rounded-lg max-w-md w-full p-6">
        <h2 className="text-xl font-bold mb-4">Editar Empresa</h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-text-2 mb-1">
              Nome
            </label>
            <input
              type="text"
              minLength={3}
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-3 py-2 border border-border-1 rounded-lg focus:ring-2 focus:ring-accent-1 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-text-2 mb-1">
              Email
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="w-full px-3 py-2 border border-border-1 rounded-lg focus:ring-2 focus:ring-accent-1 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-text-2 mb-1">
              Status
            </label>
            <select
              value={formData.status}
              onChange={(e) => setFormData({ ...formData, status: e.target.value as TenantStatus })}
              className="w-full px-3 py-2 border border-border-1 rounded-lg focus:ring-2 focus:ring-accent-1 focus:border-transparent"
            >
              <option value="trial">Trial</option>
              <option value="active">Ativo</option>
              <option value="inactive">Inativo</option>
              <option value="suspended">Suspenso</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-text-2 mb-1">
              Dia do Ciclo de Faturamento
            </label>
            <select
              value={formData.billingCycleDay}
              onChange={(e) => setFormData({ ...formData, billingCycleDay: parseInt(e.target.value) })}
              className="w-full px-3 py-2 border border-border-1 rounded-lg focus:ring-2 focus:ring-accent-1 focus:border-transparent"
            >
              {Array.from({ length: 28 }, (_, i) => i + 1).map((day) => (
                <option key={day} value={day}>
                  Dia {day}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-text-3">
              Define o dia do mês em que o ciclo de faturamento reinicia para esta empresa.
            </p>
          </div>

          <div className="flex justify-end space-x-3 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-border-1 rounded-lg hover:bg-bg-3/50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-accent-1 text-white rounded-lg hover:bg-accent-1/90"
            >
              Salvar Alterações
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
